import { AppConfig } from '@core/utils/config';
import { UserId } from '@business/entities/account';
import { Paginate } from '@business/entities/common';
import { OrgId } from '@business/entities/organization';
import { SenderID } from '@business/entities/sender';
import {
  CompactSupportTicketResponse,
  SupportTicketPublicRequest,
  SupportTicketRequest,
  SupportTicketResponse,
  SupportTicketStatus,
  TestTicketRequest,
  TicketAdditionalResponse,
  TicketFileId,
  TicketFileResponse,
  TicketId,
  TicketOrgRequest,
  TicketSearchParams,
} from '@business/entities/SupportTicket';
import { TicketReplyId, TicketReplyRequest, TicketReplyResponse } from '@business/entities/SupportTicket/TicketReplyResponse';
import { TenantId } from '@business/entities/tenant';
import { Config } from '@config';
import { R } from '@core/utils/r';
import { Endpoint } from '../endpoint';

/**
 * Support Ticket test API
 */
export function testTicket(data: TestTicketRequest) {
  return Endpoint.post<SupportTicketResponse>(`@gc/support-ticket/test`, { data });
}

/**
 * Support Ticket API
 */

export function fetchTickets(params: TicketSearchParams) {
  return Endpoint.get<Paginate<CompactSupportTicketResponse, TicketAdditionalResponse>>(`@gc/support-ticket`, { params: R.objectCompact(params) });
}

export function fetchTicket(id: TicketId) {
  return Endpoint.get<SupportTicketResponse>(`@gc/support-ticket/ticket/${id}`);
}

export function createTicket(data: SupportTicketRequest) {
  return Endpoint.post<SupportTicketResponse>(`@gc/support-ticket`, { data });
}

export function createPublicTicket(data: SupportTicketPublicRequest, files: File[]) {
  const formData = new FormData();
  formData.append('name', data.name);
  formData.append('email', data.email);
  formData.append('body', data.body);
  formData.append('subject', data.subject);
  formData.append('toEmail', data.toEmail);
  for (let i = 0; i < files.length; i++) {
    formData.append('files[' + i + ']', files[i]);
  }

  return Endpoint.post<SupportTicketResponse>(`@gc/support`, {
    formData,
    hasFiles: true,
    headers: { 'X-Tenant': Config.GC_S98_TENANT, companyId: '1' },
  });
}

export function updateTicket(id: TicketId, data: SupportTicketRequest) {
  return Endpoint.patch<CompactSupportTicketResponse>(`@gc/support-ticket/${id}`, { data });
}

export function deleteTicket(id: TicketId) {
  return Endpoint.delete<void>(`@gc/support-ticket/${id}`);
}

export function fetchTicketReply(replyId: TicketReplyId) {
  return Endpoint.get<TicketReplyResponse>(`@gc/support-ticket/reply/${replyId}`);
}

export function createTicketReply(id: TicketId, data: TicketReplyRequest, files: File[], storageKey?: string) {
  const formData = new FormData();
  formData.append('id', id);
  formData.append('body', data.body);
  formData.append('responseType', data.responseType);
  formData.append('storageKey', storageKey || '');
  formData.append('senderId', data.senderId || '0');

  for (let i = 0; i < files.length; i++) {
    formData.append('files[' + i + ']', files[i]);
  }
  for (let i = 0; i < (data.replyTo! && data.replyTo!.length); i++) {
    formData.append('replyTo[' + i + ']', data.replyTo![i]);
  }
  for (let i = 0; i < (data.replyCc! && data.replyCc!.length); i++) {
    formData.append('replyCc[' + i + ']', data.replyCc![i]);
  }
  for (let i = 0; i < (data.replyBcc! && data.replyBcc!.length); i++) {
    formData.append('replyBcc[' + i + ']', data.replyBcc![i]);
  }

  return Endpoint.post<TicketReplyResponse>(`@gc/support-ticket/reply/${id}`, {
    formData: formData,
    hasFiles: true,
  });
}

export function createTicketNote(id: TicketId, userId: UserId[], body: string, senderId: SenderID, files: File[], storageKey?: string) {
  const formData = new FormData();
  formData.append('id', id);
  formData.append('body', body);
  formData.append('senderId', senderId);
  formData.append('storageKey', storageKey ? storageKey : '');
  for (let i = 0; i < userId.length; i++) {
    formData.append('userId[' + i + ']', userId[i]);
  }
  for (let i = 0; i < files.length; i++) {
    formData.append('files[' + i + ']', files[i]);
  }

  return Endpoint.post<TicketReplyResponse>(`@gc/support-ticket/internal-note/${id}`, {
    formData: formData,
    hasFiles: true,
  });
}

export function createEscalateReply(id: TicketId, body: string, files: File[], storageKey?: string) {
  const formData = new FormData();
  formData.append('id', id);
  formData.append('body', body);
  formData.append('storageKey', storageKey || '');
  for (let i = 0; i < files.length; i++) {
    formData.append('files[' + i + ']', files[i]);
  }

  return Endpoint.post<TicketReplyResponse>(`@gc/support-ticket/escalate-reply/${id}`, {
    formData: formData,
    hasFiles: true,
  });
}

export function updateTicketReply(replyId: TicketReplyId, body: string) {
  return Endpoint.patch<TicketReplyResponse>(`@gc/support-ticket/reply/${replyId}`, { data: { body } });
}

export function splitTicket(replyId: TicketReplyId) {
  return Endpoint.post<SupportTicketResponse>(`@gc/support-ticket/split/${replyId}`);
}

export function deleteTicketReply(replyId: TicketReplyId) {
  return Endpoint.delete<SupportTicketResponse>(`@gc/support-ticket/reply/${replyId}`);
}

export function escalateTicket(id: TicketId, tenantId: TenantId) {
  return Endpoint.post<SupportTicketResponse>(`@gc/support-ticket/escalate/${id}`, { data: { tenantId } });
}
export function removeEscalated(id: TicketId) {
  return Endpoint.delete<SupportTicketResponse>(`@gc/support-ticket/escalate/${id}`, { data: {} });
}

export function undoReply(replyId: TicketReplyId) {
  return Endpoint.post<void>(`@gc/support-ticket/undo-reply/${replyId}`);
}

export function uploadTicketFiles(id: TicketId) {
  return Endpoint.post<TicketFileResponse>(`@gc/support-ticket/files/${id}`);
}

export function fetchTicketFiles(id: TicketId) {
  return Endpoint.get<TicketFileResponse[]>(`@gc/support-ticket/files/${id}`);
}
export function deleteTicketFile(fileId: TicketFileId) {
  return Endpoint.delete<void>(`@gc/support-ticket/file/${fileId}`);
}

export function updateTicketAction(id: TicketId, action: string, actionId: string | any, compact = false, closeAllTasks?: boolean) {
  return Endpoint.post<SupportTicketResponse>(`@gc/support-ticket/actions/${id}`, { data: { action, actionId, compact, closeAllTasks } });
}

export function updateTicketOrg(id: TicketId, orgId: OrgId, data: TicketOrgRequest) {
  return Endpoint.post<SupportTicketResponse>(`@gc/support-ticket/update-org/${id}/${orgId}`, { data });
}

export function bulkAction(ids: TicketId[], status: SupportTicketStatus, closeAllTasks?: boolean) {
  return Endpoint.post<void>(`@gc/support-ticket/bulk-action`, { data: { ids, status, closeAllTasks } });
}

export function getBulkTickets(ids: TicketId[]) {
  return Endpoint.get<CompactSupportTicketResponse[]>(`@gc/support-ticket/bulk`, { params: { ids } });
}
export function suggestedResponse(id: TicketId, regenerate = false) {
  return Endpoint.get<any[]>(`@gc/support-ticket/suggested-response/${id}`, { params: { regenerate } });
}

export function storeReplyUsage(id: TicketId) {
  return Endpoint.post<SupportTicketResponse>(`@gc/support-ticket/reply-usage/${id}`);
}
